import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { parseLocalDate } from '../../../utils/dateUtils';

const PhaseTimelineCard = ({ phase, isFirst, isLast, onEdit, onDelete, onInlineSave, conflictCount }) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDates, setIsEditingDates] = useState(false);
  const [nameValue, setNameValue] = useState(phase?.name || '');
  const [startDate, setStartDate] = useState(phase?.startDate ? phase?.startDate?.slice(0, 10) : '');
  const [endDate, setEndDate] = useState(phase?.endDate ? phase?.endDate?.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const nameInputRef = useRef(null);

  // Sync local state when phase prop changes
  useEffect(() => {
    setNameValue(phase?.name || '');
    setStartDate(phase?.startDate ? phase?.startDate?.slice(0, 10) : '');
    setEndDate(phase?.endDate ? phase?.endDate?.slice(0, 10) : '');
  }, [phase?.name, phase?.startDate, phase?.endDate]);

  useEffect(() => {
    if (isEditingName && nameInputRef?.current) {
      nameInputRef?.current?.focus();
      nameInputRef?.current?.select();
    }
  }, [isEditingName]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getProgressPercentage = () => {
    if (phase?.totalDeliverables === 0) return 0;
    return Math.round((phase?.completedDeliverables / phase?.totalDeliverables) * 100);
  };

  const handleSaveName = async () => {
    const trimmed = nameValue?.trim();
    if (!trimmed || trimmed === phase?.name) {
      setNameValue(phase?.name || '');
      setIsEditingName(false);
      return;
    }
    setSaving(true);
    try {
      await onInlineSave?.(phase?.id, { name: trimmed });
    } finally {
      setSaving(false);
      setIsEditingName(false);
    }
  };

  const handleCancelName = () => {
    setNameValue(phase?.name || '');
    setIsEditingName(false);
  };

  const handleSaveDates = async () => {
    if (!startDate || !endDate) return;
    if (startDate === (phase?.startDate?.slice(0, 10)) && endDate === (phase?.endDate?.slice(0, 10))) {
      setIsEditingDates(false);
      return;
    }
    setSaving(true);
    try {
      await onInlineSave?.(phase?.id, { startDate, endDate });
    } finally {
      setSaving(false);
      setIsEditingDates(false);
    }
  };

  const handleCancelDates = () => {
    setStartDate(phase?.startDate ? phase?.startDate?.slice(0, 10) : '');
    setEndDate(phase?.endDate ? phase?.endDate?.slice(0, 10) : '');
    setIsEditingDates(false);
  };

  const handleNameKeyDown = (e) => {
    if (e?.key === 'Enter') handleSaveName();
    if (e?.key === 'Escape') handleCancelName();
  };

  return (
    <div className="relative">
      {!isLast && (
        <div className="absolute left-6 top-16 bottom-0 w-0.5 bg-border hidden lg:block" />
      )}
      <div className={`bg-card rounded-xl border p-4 md:p-6 hover:shadow-md transition-shadow ${
        conflictCount > 0 ? 'border-red-300' : 'border-border'
      }`}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              phase?.status === 'completed' ? 'bg-green-100' :
              phase?.status === 'in-progress' ? 'bg-blue-100' : 'bg-gray-100'
            }`}>
              <Icon 
                name={phase?.status === 'completed' ? 'CheckCircle2' : 'Circle'} 
                size={24}
                color={
                  phase?.status === 'completed' ? '#27AE60' :
                  phase?.status === 'in-progress' ? '#3B5166' : '#7F8C8D'
                }
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="flex-1 min-w-0">
                {/* Inline Phase Name Editing */}
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  {isEditingName ? (
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <input
                        ref={nameInputRef}
                        type="text"
                        value={nameValue}
                        onChange={(e) => setNameValue(e?.target?.value)}
                        onKeyDown={handleNameKeyDown}
                        disabled={saving}
                        className="flex-1 min-w-0 text-lg md:text-xl font-semibold text-foreground bg-background border border-primary rounded-md px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                      <button
                        onClick={handleSaveName}
                        disabled={saving}
                        className="p-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                        aria-label="Save name"
                      >
                        <Icon name="Check" size={14} />
                      </button>
                      <button
                        onClick={handleCancelName}
                        disabled={saving}
                        className="p-1.5 bg-muted text-muted-foreground rounded-md hover:bg-muted/80 transition-colors disabled:opacity-50"
                        aria-label="Cancel name edit"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsEditingName(true)}
                      className="group flex items-center gap-1.5 text-left"
                      title="Click to edit phase name"
                    >
                      <h3 className="text-lg md:text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                        {phase?.name}
                      </h3>
                      <Icon
                        name="Pencil"
                        size={13}
                        color="var(--color-muted-foreground)"
                        className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      />
                    </button>
                  )}
                  {conflictCount > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold border border-red-200">
                      <Icon name="AlertTriangle" size={11} />
                      {conflictCount} Conflict{conflictCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {phase?.description}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => onEdit(phase)}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                  aria-label="Edit phase"
                >
                  <Icon name="Edit2" size={18} color="var(--color-muted-foreground)" />
                </button>
                <button
                  onClick={() => onDelete(phase?.id)}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  aria-label="Delete phase"
                >
                  <Icon name="Trash2" size={18} color="#E74C3C" />
                </button>
              </div>
            </div>

            {/* Inline Date Editing */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {isEditingDates ? (
                <>
                  <div className="md:col-span-2 flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
                      <label className="text-xs text-muted-foreground font-medium">Start</label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e?.target?.value)}
                        disabled={saving}
                        className="text-sm text-foreground bg-background border border-primary rounded-md px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="CalendarCheck" size={16} color="var(--color-muted-foreground)" />
                      <label className="text-xs text-muted-foreground font-medium">End</label>
                      <input
                        type="date"
                        value={endDate}
                        min={startDate}
                        onChange={(e) => setEndDate(e?.target?.value)}
                        disabled={saving}
                        className="text-sm text-foreground bg-background border border-primary rounded-md px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={handleSaveDates}
                        disabled={saving || !startDate || !endDate}
                        className="p-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
                        aria-label="Save dates"
                      >
                        <Icon name="Check" size={14} />
                      </button>
                      <button
                        onClick={handleCancelDates}
                        disabled={saving}
                        className="p-1.5 bg-muted text-muted-foreground rounded-md hover:bg-muted/80 transition-colors disabled:opacity-50"
                        aria-label="Cancel date edit"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Package" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm text-muted-foreground">
                      {phase?.completedDeliverables}/{phase?.totalDeliverables} Deliverables
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setIsEditingDates(true)}
                    className="group flex items-center gap-2 text-left"
                    title="Click to edit start date"
                  >
                    <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm text-muted-foreground group-hover:text-primary transition-colors">
                      {parseLocalDate(phase?.startDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <Icon name="Pencil" size={11} color="var(--color-muted-foreground)" className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>

                  <button
                    onClick={() => setIsEditingDates(true)}
                    className="group flex items-center gap-2 text-left"
                    title="Click to edit end date"
                  >
                    <Icon name="CalendarCheck" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm text-muted-foreground group-hover:text-primary transition-colors">
                      {parseLocalDate(phase?.endDate)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <Icon name="Pencil" size={11} color="var(--color-muted-foreground)" className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>

                  <div className="flex items-center gap-2">
                    <Icon name="Package" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm text-muted-foreground">
                      {phase?.completedDeliverables}/{phase?.totalDeliverables} Deliverables
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(phase?.status)}`}>
                  {phase?.status?.charAt(0)?.toUpperCase() + phase?.status?.slice(1)?.replace('-', ' ')}
                </span>
                <span className="text-sm font-medium text-foreground">
                  {getProgressPercentage()}% Complete
                </span>
              </div>

              <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${
                    phase?.status === 'completed' ? 'bg-green-500' :
                    phase?.status === 'in-progress' ? 'bg-blue-500' : 'bg-gray-400'
                  }`}
                  style={{ width: `${getProgressPercentage()}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhaseTimelineCard;